import { useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function WarningsModal({ isOpen, warnings = [], onClose }) {
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const warningsScrollRef = useRef(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedModalImage(null);
    setSavedScrollPosition(0);
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 text-left"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-[12px] max-w-6xl w-full mx-4 relative max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-text-primary text-[20px]">
              {selectedModalImage
                ? "Warning Details"
                : `Warnings (${warnings.length})`}
            </h2>
            <div className="flex items-center space-x-2">
              {selectedModalImage && (
                <button
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium px-3 py-1 rounded-[6px] hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setSelectedModalImage(null);
                    setTimeout(() => {
                      if (warningsScrollRef.current) {
                        warningsScrollRef.current.scrollTop =
                          savedScrollPosition;
                      }
                    }, 10);
                  }}
                >
                  ← Back to List
                </button>
              )}
              <button
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={handleClose}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div
          ref={warningsScrollRef}
          className="overflow-y-auto max-h-[calc(90vh-100px)]"
        >
          {selectedModalImage ? (
            <div className="p-6 flex items-center justify-center min-h-[500px]">
              <img
                src={selectedModalImage}
                alt="Enlarged warning detail"
                className="max-w-full max-h-[calc(90vh-250px)] object-contain rounded-[8px] shadow-lg"
              />
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-6">
                {warnings.map((warning, index) => (
                  <div
                    key={`${warning.message || "warning"}-${index}`}
                    className="border border-gray-200 rounded-[12px] p-6 bg-gray-50"
                  >
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="flex-shrink-0 mt-1">
                        <span className="text-yellow-500 text-xl">⚠️</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-text-primary text-base leading-[1.5]">
                          <span className="font-semibold text-[#6b7280] text-sm">
                            Warning {index + 1}:
                          </span>
                          <br />
                          {warning.message}
                        </p>
                      </div>
                    </div>

                    {warning.sidesData && warning.sidesData.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-text-primary text-sm mb-3">
                          Related Images:
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {warning.sidesData.map((side, sideIndex) => (
                            <div
                              key={`${side.name || "side"}-${side.imageUrl || sideIndex}`}
                              className="bg-white rounded-[8px] border border-gray-200 p-3"
                            >
                              <div className="text-center mb-2">
                                <span className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-text-primary text-[13px]">
                                  {side.name}
                                </span>
                              </div>
                              {side.imageUrl ? (
                                <div
                                  className="relative group cursor-pointer"
                                  onClick={() => {
                                    if (warningsScrollRef.current) {
                                      setSavedScrollPosition(
                                        warningsScrollRef.current.scrollTop
                                      );
                                    }
                                    setSelectedModalImage(side.imageUrl);
                                  }}
                                >
                                  <img
                                    src={side.imageUrl}
                                    alt={`Warning for ${side.name}`}
                                    className="w-full h-48 object-cover rounded-[6px] transition-transform group-hover:scale-105"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-[6px] transition-all flex items-center justify-center pointer-events-none">
                                    <span className="text-white bg-black bg-opacity-0 group-hover:bg-opacity-70 px-3 py-1 rounded-[4px] text-xs font-medium transition-all">
                                      Click to enlarge
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-48 bg-gray-200 rounded-[6px] flex items-center justify-center text-gray-400 text-xs">
                                  No image available
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
